const EXPECTATIONS_BY_NAME = {
  // Local happy paths
  'inline-linear': {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted', 'adimpression'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
  },
  'inline-linear-skippable': {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
    notes: 'Skippable ads may end with adskipped instead of full quartile progression.',
  },
  'wrapper-linear': {
    expectedOutcome: 'success',
    requiredEvents: ['adtagloaded', 'adstarted'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
  },
  'inline-nonlinear': {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
    notes: 'Non-linear creatives can differ in progress/completion semantics.',
  },
  'ad-pod': {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted'],
    forbiddenEvents: ['aderror'],
    minOccurrences: { adstarted: 2 },
    minTrackingRequests: 1,
  },
  'companion-linear': {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted', 'adimpression'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
  },

  // Intentionally broken presets
  'empty-vast': {
    expectedOutcome: 'error',
    requiredEvents: ['aderror'],
    notes: 'No-ad VAST should fail in a controlled way.',
  },
  'invalid-url': {
    expectedOutcome: 'error',
    requiredEvents: ['aderror'],
    notes: '404/non-existent URL should produce network-level aderror.',
  },
  'malformed-xml': {
    expectedOutcome: 'error',
    requiredEvents: ['aderror'],
    notes: 'Malformed XML should trigger parser/error handling path.',
  },
};

function _defaultExpectationForPreset(preset) {
  const lowerCategory = String(preset.category || '').toLowerCase();
  const isRemoteLike = lowerCategory.includes('remote') || lowerCategory.includes('vpaid') || lowerCategory.includes('simid');

  return {
    expectedOutcome: 'success',
    requiredEvents: ['adstarted'],
    forbiddenEvents: ['aderror'],
    minTrackingRequests: 1,
    environmentDependent: isRemoteLike,
    notes: isRemoteLike
      ? 'Remote/SIMID/VPAID presets may fail due to CORS/runtime support and should be treated as environment-dependent.'
      : undefined,
  };
}

export function getPresetExpectation(preset) {
  return {
    ..._defaultExpectationForPreset(preset),
    ...(EXPECTATIONS_BY_NAME[preset.name] || {}),
  };
}

export function evaluatePresetAgainstExpectation({ preset, expectation, report }) {
  const events = report.events || [];
  const eventTypes = events.map(e => e.type);
  const hasEvent = (type) => eventTypes.includes(type);
  const countEvent = (type) => eventTypes.filter(t => t === type).length;

  const checks = [];
  const addCheck = (name, passed, details = '') => checks.push({ name, passed, details });

  const hasError = hasEvent('aderror') || report.lifecycle?.status === 'error';
  const hasStart = hasEvent('adstarted');

  if (expectation.expectedOutcome === 'error') {
    addCheck('Expected error outcome', hasError, hasError ? 'aderror observed' : 'Expected aderror was not observed');
  } else {
    addCheck('Expected success outcome', hasStart && !hasError, !hasStart ? 'adstarted missing' : hasError ? 'aderror observed unexpectedly' : 'adstarted observed without aderror');
  }

  for (const ev of expectation.requiredEvents || []) {
    addCheck(`Required event: ${ev}`, hasEvent(ev), hasEvent(ev) ? 'present' : 'missing');
  }

  for (const ev of expectation.forbiddenEvents || []) {
    addCheck(`Forbidden event: ${ev}`, !hasEvent(ev), hasEvent(ev) ? 'present unexpectedly' : 'not observed');
  }

  const minOccurrences = expectation.minOccurrences || {};
  for (const [eventName, min] of Object.entries(minOccurrences)) {
    const count = countEvent(eventName);
    addCheck(`Minimum occurrences for ${eventName} >= ${min}`, count >= min, `observed ${count}`);
  }

  if (typeof expectation.minTrackingRequests === 'number') {
    const trackingCount = (report.networkLog || []).length;
    addCheck(
      `Minimum tracking requests >= ${expectation.minTrackingRequests}`,
      trackingCount >= expectation.minTrackingRequests,
      `observed ${trackingCount}`
    );
  }

  const failedChecks = checks.filter(c => !c.passed);
  let status = failedChecks.length === 0 ? 'match' : 'mismatch';
  if (status === 'mismatch' && expectation.environmentDependent) {
    status = 'inconclusive';
  }

  return {
    status,
    checks,
    failedChecks,
    observed: {
      lifecycleStatus: report.lifecycle?.status,
      eventCount: events.length,
      networkCount: (report.networkLog || []).length,
      hasAdStarted: hasStart,
      hasAdError: hasError,
      errorCodes: events.filter(e => e.type === 'aderror').map(e => e.data?.code).filter(code => code != null),
    },
    notes: expectation.notes || null,
    preset: {
      name: preset.name,
      label: preset.label,
      category: preset.category,
      url: preset.url,
    },
  };
}
